const DEFAULT_BG = 'rgba(254, 249, 195, 0.5)';
const DEFAULT_HUE = 45;

const FNV_OFFSET = 2166136261;
const FNV_PRIME = 16777619;
const HUE_MIX = 2654435761;

/**
 * 32-bit FNV-1a hash of a string. Used to derive a stable numeric value from version
 * strings; similar strings produce very different hashes (good avalanche).
 *
 * @param str - Input string to hash.
 * @returns Unsigned 32-bit integer hash.
 */
function hashString(str: string): number {
    let hash = FNV_OFFSET;
    for (let i = 0; i < str.length; i++) {
        hash ^= str.charCodeAt(i);
        hash = Math.imul(hash, FNV_PRIME);
    }
    return hash >>> 0;
}

/**
 * Deterministic HSL hue in [0, 360) for a version string. Uses FNV-1a hash plus
 * a mixing step so similar strings (e.g. "java,python" vs "cpp,java") map to
 * distinct hues.
 *
 * @param version - Version string (e.g. "cpp", "java,python"), or null/undefined.
 * @returns Hue in degrees, or DEFAULT_HUE when version is empty.
 */
function getHue(version: string | null | undefined): number {
    if (!version) return DEFAULT_HUE;
    const hash = hashString(version);
    const mixed = Math.imul(hash, HUE_MIX) >>> 0;
    return mixed % 360;
}

/**
 * Deterministic pastel background color for a version string.
 * Returns the default yellow when the version filter is empty/null.
 */
export function getVersionColor(version: string | null | undefined): string {
    if (!version) return DEFAULT_BG;
    return `hsla(${getHue(version)}, 70%, 85%, 0.5)`;
}

/**
 * Deterministic badge colors (solid bg, hover bg, focus ring) for a version string.
 * Returns default yellow tones when the version is empty/null.
 */
export function getVersionBadgeColors(version: string | null | undefined): {
    bg: string;
    hover: string;
    ring: string;
} {
    const hue = getHue(version);
    return {
        bg: `hsl(${hue}, 70%, 50%)`,
        hover: `hsl(${hue}, 70%, 42%)`,
        ring: `hsl(${hue}, 70%, 60%)`,
    };
}
